import { IAgoraRTCClient, IRemoteVideoTrack } from 'agora-rtc-sdk-ng';
import useCallListener from 'hooks/call-queue/useCallListener';
import useChatListener from 'hooks/call-queue/useChatListener';
import { createContext, ReactElement, useState, useEffect, useRef, useCallback } from 'react';
import { VideoSessionContextType } from 'types/auth';
import { CallStatus } from 'types/auth';
import { useLocation } from 'react-router-dom';
import { registerBusyCheck } from 'hooks/useIdleSignOut';

const VideoSessionContext = createContext<VideoSessionContextType | null>(null);

export const VideoSessionProvider = ({ children }: { children: ReactElement }) => {
    const [callUid, setCallUid] = useState<string | null>(null);
    const [client, setClient] = useState<IAgoraRTCClient | null>(null);
    const session = useCallListener(callUid);
    const messages = useChatListener(callUid);
    const [localAudioTrack, setLocalAudioTrack] = useState<any>(null);
    const [remoteVideo, setRemoteVideo] = useState<IRemoteVideoTrack | null | undefined>(null);
    const location = useLocation();
    
    // Keep persistent references to avoid losing them on re-renders
    const clientRef = useRef<IAgoraRTCClient | null>(null);
    const audioTrackRef = useRef<any>(null);
    const remoteVideoRef = useRef<IRemoteVideoTrack | null | undefined>(null);
    const sessionStateRef = useRef<{
        isConnected: boolean;
        channelName?: string;
        appId?: string;
        userToken?: string;
        uidNumber?: number;
    }>({
        isConnected: false
    });

    // A connected call has no mouse or keyboard input, but the user is busy: keep the
    // idle sign-out from ending the session mid-call. Polls the client's state rather
    // than listening for events, because VideoCall calls removeAllListeners() on it.
    useEffect(() => registerBusyCheck(() => {
        const state = clientRef.current?.connectionState;
        return state === 'CONNECTED' || state === 'RECONNECTING';
    }), []);

    // Sync refs with state
    useEffect(() => {
        clientRef.current = client;
    }, [client]);

    useEffect(() => {
        audioTrackRef.current = localAudioTrack;
    }, [localAudioTrack]);

    useEffect(() => {
        remoteVideoRef.current = remoteVideo;
    }, [remoteVideo]);

    // Enhanced setClient that maintains persistence
    const setClientPersistent = useCallback((newClient: IAgoraRTCClient | null) => {
        clientRef.current = newClient;
        setClient(newClient);
    }, []);

    // Enhanced setLocalAudioTrack that maintains persistence
    const setLocalAudioTrackPersistent = useCallback((newTrack: any) => {
        audioTrackRef.current = newTrack;
        setLocalAudioTrack(newTrack);
    }, []);

    // Enhanced setRemoteVideo that maintains persistence
    const setRemoteVideoPersistent = useCallback((newVideo: IRemoteVideoTrack | null | undefined) => {
        remoteVideoRef.current = newVideo;
        setRemoteVideo(newVideo);
    }, []);

    // Function to check if client is connected and ready
    const isClientReady = useCallback((): boolean => {
        return Boolean(clientRef.current && 
               clientRef.current.connectionState === 'CONNECTED' && 
               sessionStateRef.current.isConnected);
    }, []);

    // Function to get current client state
    const getClientState = useCallback(() => {
        return {
            client: clientRef.current,
            audioTrack: audioTrackRef.current,
            remoteVideo: remoteVideoRef.current,
            isConnected: isClientReady(),
            sessionState: sessionStateRef.current
        };
    }, [isClientReady]);

    // Function to update session state
    const updateSessionState = useCallback((updates: Partial<typeof sessionStateRef.current>) => {
        sessionStateRef.current = { ...sessionStateRef.current, ...updates };
    }, []);

    // Optimized function to reconnect video streams only when needed
    const reconnectVideoStreams = useCallback(() => {
        if (!clientRef.current || clientRef.current.connectionState !== 'CONNECTED') {
            return;
        }

        // Check for existing remote users and their tracks
        const remoteUsers = clientRef.current.remoteUsers;
        
        if (remoteUsers.length === 0) return;
        
        let reconnected = false;
        remoteUsers.forEach(remoteUser => {
            if (remoteUser.videoTrack && !remoteVideoRef.current) {
                setRemoteVideoPersistent(remoteUser.videoTrack);
                reconnected = true;
            }
            if (remoteUser.audioTrack) {
                remoteUser.audioTrack.play();
            }
        });
        
        return reconnected;
    }, [setRemoteVideoPersistent]);

    // Cleanup function for when session ends
    const cleanupSession = useCallback(() => {
        console.log('VideoSessionContext: Starting cleanup...');
        
        // Skip Agora cleanup for CHAT sessions but still clear session data
        if (session?.sessionType === 'CHAT') {
            console.log('VideoSessionContext: CHAT session detected, skipping Agora cleanup');
            // Reset only non-Agora state for chat sessions
            setCallUid(null);
            console.log('VideoSessionContext: Chat cleanup completed');
            return;
        }
        
        if (audioTrackRef.current) {
            try {
                audioTrackRef.current.stop();
                audioTrackRef.current.close();
                console.log('VideoSessionContext: Audio track cleaned up');
            } catch (error) {
                console.warn('VideoSessionContext: Error cleaning up audio track:', error);
            }
        }
        
        if (clientRef.current) {
            try {
                // Only unpublish and leave if we're actually connected
                if (clientRef.current.connectionState === 'CONNECTED') {
                    clientRef.current.unpublish();
                    clientRef.current.leave();
                    console.log('VideoSessionContext: Client cleaned up');
                } else {
                    console.log('VideoSessionContext: Client not connected, skipping unpublish/leave');
                }
            } catch (error) {
                console.warn('VideoSessionContext: Error cleaning up client:', error);
            }
        }
        
        // Reset all state - ALWAYS clear callUid to allow new calls
        setCallUid(null); // Clear the call UID to allow new calls
        setClient(null);
        setLocalAudioTrack(null);
        setRemoteVideo(null);
        clientRef.current = null;
        audioTrackRef.current = null;
        remoteVideoRef.current = null;
        sessionStateRef.current = { isConnected: false };
        
        console.log('VideoSessionContext: Cleanup completed');
    }, []);

    // Force cleanup function that clears everything regardless of session status
    const forceCleanupSession = useCallback(() => {
        console.log('VideoSessionContext: Starting force cleanup...');
        
        if (audioTrackRef.current) {
            try {
                audioTrackRef.current.stop();
                audioTrackRef.current.close();
                console.log('VideoSessionContext: Audio track force cleaned up');
            } catch (error) {
                console.warn('VideoSessionContext: Error force cleaning up audio track:', error);
            }
        }
        
        if (clientRef.current) {
            try {
                // Only unpublish and leave if we're actually connected
                if (clientRef.current.connectionState === 'CONNECTED') {
                    clientRef.current.unpublish();
                    clientRef.current.leave();
                    console.log('VideoSessionContext: Client force cleaned up');
                } else {
                    console.log('VideoSessionContext: Client not connected, skipping unpublish/leave');
                }
            } catch (error) {
                console.warn('VideoSessionContext: Error force cleaning up client:', error);
            }
        }
        
        // Reset all state
        setCallUid(null); // Clear the call UID to allow new calls
        setClient(null);
        setLocalAudioTrack(null);
        setRemoteVideo(null);
        clientRef.current = null;
        audioTrackRef.current = null;
        remoteVideoRef.current = null;
        sessionStateRef.current = { isConnected: false };
        
        console.log('VideoSessionContext: Force cleanup completed');
    }, []);

    // Optimized navigation handling
    useEffect(() => {
        const isOnSessionPage = location.pathname.startsWith('/session/');
        const currentSessionId = location.pathname.split('/session/')[1];
        
        // Handle navigation away from session pages
        if (!isOnSessionPage && callUid) {
            const shouldCleanup = session?.status === CallStatus.COMPLETED || 
                                 session?.status === CallStatus.USER_LEFT ||
                                 session?.status === CallStatus.MANUAL_DISCONNECT ||
                                 session?.status === CallStatus.MISSED_CALL;
            
            if (shouldCleanup) {
                forceCleanupSession();
            }
            return;
        }
        
        // Handle session page scenarios
        if (isOnSessionPage && currentSessionId) {
            // Switching to different session
            if (callUid && currentSessionId !== callUid) {
                const shouldCleanupPrevious = session?.status === CallStatus.COMPLETED || 
                                            session?.status === CallStatus.USER_LEFT ||
                                            session?.status === CallStatus.MANUAL_DISCONNECT ||
                                            session?.status === CallStatus.MISSED_CALL;
                
                if (shouldCleanupPrevious) {
                    forceCleanupSession();
                }
                setCallUid(currentSessionId);
            }
            // Setting callUid for new session
            else if (!callUid) {
                setCallUid(currentSessionId);
            }
        }
    }, [location.pathname, callUid, session?.status, forceCleanupSession]);

    return (
        <VideoSessionContext.Provider value={{
            callUid,
            setCallUid,
            session,
            messages,
            client: clientRef.current || client,
            setClient: setClientPersistent,
            localAudioTrack: audioTrackRef.current || localAudioTrack,
            setLocalAudioTrack: setLocalAudioTrackPersistent,
            remoteVideo: remoteVideoRef.current || remoteVideo,
            setRemoteVideo: setRemoteVideoPersistent,
            cleanupSession,
            forceCleanupSession,
            isClientReady,
            getClientState,
            updateSessionState,
            reconnectVideoStreams
        }}>
            {children}
        </VideoSessionContext.Provider>
    );
};

export default VideoSessionContext;